import { addCalendarMonths } from "@oysterhr/common"

import {
  User,
  CompanyEquityPlanEnumType,
  Engagement,
  EngagementState,
  EngagementProbationPeriodType,
  CompanyAvailableFeatures,
  Company,
} from "@oysterhr/schema"
import type { TFunction } from "react-i18next"

export const isMobile = () => {
  // width taken from tailwind md utility
  return window.innerWidth < 768
}

/**
 * Calculates probation end date given the value and type of probation period
 * @param {number} value
 * @param {EngagementProbationPeriodType} type
 * @param {Date} startDate
 * @returns {Date} calculated probation end date
 */
export function getProbationPeriodEndDate(
  value: number | undefined,
  type: EngagementProbationPeriodType | undefined,
  startDate: Date | undefined,
): Date | null {
  let probationEnd = null
  if (value && !isNaN(value) && type) {
    // eslint-disable-next-line oyster/frog-forbidden-date
    probationEnd = startDate ? new Date(startDate) : new Date()
    // The probation end date is included as a probation day.
    // We need to remove one day not to result in an extra day of probation.
    switch (type) {
      case EngagementProbationPeriodType.Days:
        probationEnd.setDate(probationEnd.getDate() + +value - 1)
        break
      case EngagementProbationPeriodType.Weeks:
        probationEnd.setDate(probationEnd.getDate() + value * 7 - 1)
        break
      case EngagementProbationPeriodType.Months:
        probationEnd = addCalendarMonths(probationEnd, value)
        probationEnd.setDate(probationEnd.getDate() - 1)
        break
    }
  }
  return probationEnd
}

/**
 * hasEquityPlan
 * @param {string} accountAspect
 * @param {import("@oysterhr/schema").User} me
 * @returns {boolean}
 */
export function hasEquityPlan(
  accountAspect: string,
  me: User,
  plans: CompanyEquityPlanEnumType[] = [],
) {
  return me.companiesAsAdmin?.some(
    company =>
      company.id === accountAspect && company.equityPlan && plans.includes(company.equityPlan),
  )
}

/**
 * Check if the given engagement is allowed access to the time off feature
 */
export function extractTimeOffSettingFromEngagement(
  engagement?: Pick<Engagement, "state" | "timeOffManagementEnabled">,
) {
  if (engagement?.state !== EngagementState.Engaged) return false

  return engagement.timeOffManagementEnabled
}

/**
 * Check if the given engagement is allowed access to the payment method management
 */
export function extractPaymentMethodManagementSettingFromEngagement(
  engagement?: Pick<Engagement, "state" | "paymentMethodManagementEnabled">,
) {
  if (engagement?.state !== EngagementState.Engaged) return false

  return engagement.paymentMethodManagementEnabled
}

/**
 * Check if time tracking feature globally enabled and for specific country
 */
export function extractTimeTrackingSettingFromEngagement(
  engagement?: Pick<Engagement, "state" | "timeTrackingEnabled">,
) {
  if (engagement?.state !== EngagementState.Engaged) return false

  return engagement.timeTrackingEnabled
}

/**
 * Returns a string representation of a number that is within the given maximum value.
 *
 * @param {string} num - A string representation of a number.
 * @param {number} [max=9999999999999.99] - The maximum value to compare the number against.
 *
 * @returns {string} - A string representation of the number that is within the given maximum value.
 */
export const withinMax = (num: string, max = 9999999999999.99): string => {
  if (num === "-") return num
  while (isNaN(Number(num)) || Number(num) > max) {
    num = num.slice(0, num.length - 1)
  }
  return num
}

/**
 * Converts a boolean value into his own corresponding string
 *
 * true -> "true"
 *
 * false -> "false"
 * @returns "true" | "false"
 */
export const booleanToTextValue = (v: string | undefined | boolean | number | null) =>
  v ? "true" : v === null || v === undefined ? "null" : "false"

/**
 * Converts "true" or "false" into the corresponding boolean value
 * If the string is not one of the two values, it returns null
 */
export const textValueToBoolean = (t?: string) =>
  t === "true" ? true : t === "false" ? false : null

/**
 * Function to convert a empty string into null
 * @returns string | null
 */
export const deblankify = (v: string) => (v === "" ? null : v)

/**
 * Function to convert a string into a float, or a null value if the string is empty
 */
export const deblankifyFloat = (v: string) => (isNaN(parseFloat(v)) ? null : parseFloat(v))

type StringLiteral<T> = T extends string ? (string extends T ? never : T) : never
/**
 * Function to return a object with the key and value set if the value exists
 * and undefined if the value doesn't exist
 * Useful for optionally adding a key/value pair to an object
 * @example
 * const obj = {
 *   someKey: 'someValue'
 *   ...optionalProperty('keyWithValue', 'someValue')
 *   ...optionalProperty('keyWithNullishValue', null)
 * }
 * obj === {
 *   someKey: "someValue",
 *   keyWithValue: "someValue"
 * }
 *
 */
export const optionalProperty = <K, V>(key: StringLiteral<K>, value: V | undefined) => {
  if (value == null) {
    return undefined
  }
  return {
    [key]: value,
  } as { [P in StringLiteral<K>]: V }
}

export function companyHasDirectDebitEnforced(company: Pick<Company, "enabledFeatures">) {
  return companyHasFeatureEnabled(company, CompanyAvailableFeatures.DirectDebitEnforced)
}

export function companyHasFeatureEnabled(
  company: Pick<Company, "enabledFeatures">,
  feature: CompanyAvailableFeatures,
) {
  return company?.enabledFeatures?.includes(feature)
}

// Generates boolean radio field options
export const booleanRadioOptions = (t: TFunction) => [
  {
    label: t("booleanOptions.label.yes", {
      ns: "Shared",
    }),
    value: booleanToTextValue(true),
  },
  {
    label: t("booleanOptions.label.no", {
      ns: "Shared",
    }),
    value: booleanToTextValue(false),
  },
]

// TODO: replace usages with lodash omit
export const omitKeys = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const newObj = { ...obj }
  // Skipping since we intend to replace this function anyway
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  keys.forEach(key => delete newObj[key])
  return newObj
}

export const scrollToElement = (elementId: string) => {
  const element = document?.getElementById?.(elementId)

  element?.scrollIntoView({
    block: "center",
    behavior: "smooth",
  })

  const removeRing = () => {
    element?.classList.remove("ring-4")
  }

  const timer = setTimeout(() => {
    element?.focus()
    element?.classList.add("ring-4")
    element?.addEventListener("blur", removeRing)
  }, 500)

  return () => {
    clearTimeout(timer)
    element?.removeEventListener("blur", removeRing)
  }
}
